import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowRightStartOnRectangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import { HandDisplay } from '../components/problems/HandDisplay';
import { VulnerabilityIndicator } from '../components/problems/VulnerabilityIndicator';
import {
  ConfirmationMessage,
  StateLabel,
  OvercallingAuctionDisplay,
} from '../components/overcalling-data-editing';
import { useOvercallingDataEditingSession } from '../hooks/useOvercallingDataEditingSession';
import { usePageTitle } from '../hooks/usePageTitle';
import { overcallingDataEditingApi } from '../api/overcallingDataEditingApi';
import type { OvercallingCandidateItem } from '../types/overcallingDataEditing';
import type { Hand as PracticeHand } from '../types/problems';
import { formatBidToken } from '../utils/formatBidToken';

// Full direction names for display (mirrors ProblemsPage).
const DIRECTION_NAMES: Record<'N' | 'E' | 'S' | 'W', string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

// Jeff's partner display names. The candidate pool is hard-filtered to the two
// Hawkes-partnership partners (OvercallingCandidatePoolRepository — Jim 74,
// Anne Rahtjen Hawkes 4664), so these two ids cover every real hand; the raw id
// is shown as a fallback for any future/unexpected partnerId.
const PARTNER_NAMES: Record<number, string> = {
  74: 'Jim',
  4664: 'Anne Rahtjen Hawkes',
};

// FR-025 (Session 2026-05-21): the "Jeff's bid" panel uses the shared
// `formatBidToken` helper from `client/src/utils/formatBidToken.ts`. The same
// helper is applied to every non-masked bid in `OvercallingAuctionDisplay.tsx`
// per FR-019a (d), so the panel value and the in-auction notation cannot
// diverge. Local alias kept for diff-minimal call sites.
const formatJeffBid = formatBidToken;

// Mirrors the ProblemsPage (Important Sequences) layout for the curation
// workflow: same outer wrapper, metadata row, gray-100 content area with
// vulnerability + hand + auction + answer containers, and a bottom action
// row. Only the controls explicitly removed by the spec — scoring labels,
// "What is your call?" container, Skip Problem / Add to Review buttons —
// are omitted. An Exit button (matches "End Session" treatment on the other
// pages) returns the editor to /dashboard.
// Introduced by feature 009-overcalling-data-editing.
//
// Session 2026-05-20 scope reduction: teaching problems are OUT OF SCOPE.
// - The "Jeff says:" section (FR-026) is removed.
// - The "Number (Serial):" header form is removed; only "Number (ID): N" survives.
// - The page-header counter is renamed from "Problem: N/Total" to "Hand: N/Total".
// - The `?serial=N` deep-link (FR-040) routes to the friendly in-page "not found"
//   UI with the URL preserved (HTTP 200, no redirect) — no API call is made.
// - Auction rendering goes through the new OvercallingAuctionDisplay wrapper
//   (FR-019a) which masks Jeff's `jeffSeq` slot with a large, bold-red "?".
export function OvercallingDataEditingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Hyphen, not an em dash: matches this page's own <h1>, the same way
  // "Ask Jeff - Review" and "3rd level bids - Review" title themselves.
  usePageTitle('Overcalling - Data editing — Hawkes Bridge');

  const {
    filter,
    items,
    currentItem,
    counterTotal,
    counterPosition,
    loading,
    autoAdvancing,
    error,
    lastAction,
    loadCandidates,
    applyKeep,
    applyReject,
    advanceNext,
    advancePrev,
  } = useOvercallingDataEditingSession();

  // Direct-link state (?id= only post-Session 2026-05-20). `?serial=` is the
  // deprecated problem-side form and is handled below as immediate "not found".
  const [directItem, setDirectItem] = useState<OvercallingCandidateItem | null>(null);
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);

  const serialParam = searchParams.get('serial');
  const idParam = searchParams.get('id');

  // Mobile Details popup (mirrors ProblemsPage pattern).
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  useEffect(() => {
    // Session 2026-05-20 FR-040: `?serial=N` is the deprecated problem-side
    // deep-link. It is no longer routed to a backend endpoint (the endpoint
    // doesn't exist). Render the friendly in-page "not found" UI immediately
    // with the URL preserved (HTTP 200, no redirect).
    if (serialParam) {
      setDirectItem(null);
      setDirectLoading(false);
      setDirectError(
        `The ?serial=${serialParam} deep-link is no longer supported. Teaching problems are no longer curated by this page.`
      );
      return;
    }
    if (!idParam) {
      setDirectItem(null);
      setDirectError(null);
      return;
    }
    setDirectLoading(true);
    setDirectError(null);
    overcallingDataEditingApi.getByTableResultId(Number(idParam))
      .then(item => setDirectItem(item))
      .catch(err => {
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          setDirectError(`Hand with ID ${idParam} is not in the Overcalling data categories.`);
        } else {
          setDirectError(err instanceof Error ? err.message : 'Failed to load item.');
        }
      })
      .finally(() => setDirectLoading(false));
  }, [serialParam, idParam]);

  // Prefer the direct-link item when present.
  const activeItem: OvercallingCandidateItem | null = directItem ?? currentItem;
  const showNotFound = (serialParam || idParam) && !directLoading && !directItem && directError;

  // Scroll to top on item change (mirrors ProblemsPage).
  const pageScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    pageScrollRef.current?.scrollTo(0, 0);
  }, [activeItem?.tableResultId]);

  // FR-030a: all five action buttons stay disabled while a curation save is in flight
  // (loading, FR-031b) AND through the subsequent 1-second auto-advance window
  // (autoAdvancing) — the timed advance must not be interrupted.
  const busy = loading || autoAdvancing;

  // Button gating per FR-032..FR-034.
  const curated = !!activeItem?.curation;
  const keepDisabled = busy || (curated && activeItem!.curation!.isDeleted === false);
  const rejectDisabled = busy || (curated && activeItem!.curation!.isDeleted === true);

  // Edge gating (Session 2026-05-29 manual-test feedback): no wrap-around on the manual nav
  // buttons — disable Previous on the first hand and Next on the last hand. counterPosition is
  // 1-based over counterTotal. (The post-curation auto-advance still wraps last -> first.)
  const atFirstItem = counterPosition <= 1;
  const atLastItem = counterPosition >= counterTotal;

  // Seat in the shape expected by shared components (N|E|S|W).
  const jeffSeat = (activeItem?.jeffSeat as 'N' | 'E' | 'S' | 'W') || 'S';

  // Exit → Data Editing landing page. Uses the same visual treatment as "End
  // Session" on the Important Sequences page; curation writes persist
  // immediately so there's nothing to save before leaving.
  const handleExit = () => navigate('/data-editing');

  // Keep / Don't Keep wrappers that also reconcile the directItem view when
  // the user is on an ?id= direct link.
  const onKeep = directItem
    ? async () => {
        if (!activeItem) return;
        const updated = await overcallingDataEditingApi.keepHand(activeItem.tableResultId);
        setDirectItem({ ...activeItem, curation: updated });
      }
    : applyKeep;

  const onReject = directItem
    ? async () => {
        if (!activeItem) return;
        const updated = await overcallingDataEditingApi.rejectHand(activeItem.tableResultId);
        setDirectItem({ ...activeItem, curation: updated });
      }
    : applyReject;

  const onNext = directItem ? () => { /* no list to advance in direct-link mode */ } : advanceNext;
  const onPrev = directItem ? () => { /* no list to step back in direct-link mode */ } : advancePrev;

  if (loading && items.length === 0 && !activeItem) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-ink-soft">Loading items...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pageScrollRef}
      className="mx-auto px-2 md:px-4 py-8 short:py-3 narrow:py-1 problems-scroll problems-scroll-height scrollable"
      style={{ maxWidth: 'min(100vw - 2rem, 75rem)' }}
    >
      {/* Desktop header: title + state label left; counter, Exit right. */}
      <div className="hidden md:flex narrow:hidden items-center gap-4 short:gap-2 mb-3 short:mb-2">
        <h1 className="text-lg sm:text-xl font-bold text-ink flex items-center gap-3">
          Overcalling - Data editing
          {activeItem && <StateLabel curation={activeItem.curation} />}
        </h1>
        <div className="ml-auto flex items-center gap-4 short:gap-2">
          {!serialParam && !idParam && (
            <div className="flex items-center bg-cream border border-line rounded-lg px-3 py-1.5 gap-3">
              <span className="text-sm text-ink-soft">
                Hand: {counterPosition}/{counterTotal}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleExit}
            className="text-sm font-medium text-ink bg-cream border border-brass hover:bg-paper rounded-md px-3 py-1.5"
            aria-label="Exit"
          >
            Exit
          </button>
        </div>
      </div>

      {/* Desktop: Data category filter directly below the banner (FR-036).
          The Refresh button re-pulls the working set for the current filter. */}
      {!serialParam && !idParam && (
        <div className="hidden md:flex narrow:hidden items-center gap-2 mb-4 short:mb-2">
          <label htmlFor="data-category-filter" className="text-sm font-bold text-ink">
            Data category:
          </label>
          <select
            id="data-category-filter"
            value={filter}
            onChange={e => loadCandidates(e.target.value as typeof filter)}
            disabled={busy}
            className="border border-line-strong rounded-md px-2 py-1 text-sm"
          >
            <option value="All">All</option>
            <option value="Edited">Edited</option>
            <option value="NotEdited">Not edited</option>
            <option value="Added">Added</option>
            <option value="Deleted">Deleted</option>
          </select>
          <button
            type="button"
            onClick={() => loadCandidates(filter)}
            disabled={busy}
            className="text-sm font-medium text-ink bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed border border-line-strong rounded-md px-3 py-1"
            aria-label="Refresh"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Mobile compact controls row. */}
      <div className="md:hidden narrow:flex flex items-center gap-2 mb-1 overflow-hidden" data-testid="compact-controls">
        {!serialParam && !idParam ? (
          <span className="text-sm">
            <span className="font-bold text-ink">{counterPosition}/{counterTotal}</span>
          </span>
        ) : (
          activeItem && (
            <span className="text-sm font-bold text-ink">
              ID: {activeItem.tableResultId}
            </span>
          )
        )}
        <div className="w-px h-4 bg-line-strong" />
        {activeItem && <StateLabel curation={activeItem.curation} />}
        {activeItem && (
          <button type="button" onClick={() => setShowDetailsPopup(prev => !prev)} className="text-ink-soft hover:text-ink-soft p-0.5" aria-label="Item details">
            <InformationCircleIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleExit}
          className="ml-auto flex-shrink-0 p-1 text-ink-soft hover:text-ink hover:bg-cream rounded"
          aria-label="Exit"
          title="Exit"
        >
          <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile filter (shown below the compact row so it doesn't crowd it). */}
      {!serialParam && !idParam && (
        <div className="md:hidden narrow:flex items-center gap-2 mb-2">
          <label htmlFor="data-category-filter-mobile" className="text-sm font-bold text-ink">
            Data category:
          </label>
          <select
            id="data-category-filter-mobile"
            value={filter}
            onChange={e => loadCandidates(e.target.value as typeof filter)}
            disabled={busy}
            className="border border-line-strong rounded-md px-2 py-1 text-sm"
          >
            <option value="All">All</option>
            <option value="Edited">Edited</option>
            <option value="NotEdited">Not edited</option>
            <option value="Added">Added</option>
            <option value="Deleted">Deleted</option>
          </select>
          <button
            type="button"
            onClick={() => loadCandidates(filter)}
            disabled={busy}
            className="text-xs font-medium text-ink bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed border border-line-strong rounded-md px-2 py-1"
            aria-label="Refresh"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Mobile Details popup */}
      {showDetailsPopup && activeItem && (
        <div className="md:hidden narrow:block fixed inset-0 z-40 bg-black/30 flex items-start justify-center pt-16 px-4" onClick={() => setShowDetailsPopup(false)}>
          <div className="bg-card rounded-lg shadow-lg p-4 w-full max-w-sm relative" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setShowDetailsPopup(false)} className="absolute top-1 right-1 text-ink-soft hover:text-ink-soft text-sm font-bold px-1" aria-label="Close">&times;</button>
            <h3 className="text-sm font-semibold text-ink mb-2">Item Details</h3>
            <div className="space-y-1 text-sm text-ink">
              <div><span className="text-ink-soft">Number:</span> <span className="font-medium">{activeItem.tableResultId}</span></div>
              <div><span className="text-ink-soft">Seat:</span> <span className="font-medium">{activeItem.jeffSeat || '—'}</span></div>
              <div><span className="text-ink-soft">Scoring:</span> <span className="font-medium">{activeItem.scoringType || 'N/A'}</span></div>
              <div><span className="text-ink-soft">Difficulty:</span> <span className="font-medium">{activeItem.difficultyLevel != null ? `${activeItem.difficultyLevel}/10` : 'N/A'}</span></div>
              {activeItem.sourceLabel && (
                <div><span className="text-ink-soft">From:</span> <span className="font-medium">{activeItem.sourceLabel}</span></div>
              )}
              {activeItem.vulnerability && (
                <div><span className="text-ink-soft">Vulnerability:</span> <span className="font-medium">{activeItem.vulnerability}</span></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error band */}
      {error && <div className="text-red-700 text-sm mb-2">{error}</div>}

      {/* Direct-link not-found message (FR-042 + FR-040 Session 2026-05-20).
          Triggered by:
          - `?serial=N` (the deprecated problem-side deep-link)
          - `?id=N` referencing an out-of-pool tableresult_id
          - any other dataset/API error during direct-link load
          The URL is preserved (no redirect); the link below routes back to the
          default view. */}
      {showNotFound && (
        <div className="p-4 border border-amber-300 bg-amber-50 rounded">
          <p className="text-amber-900">{directError}</p>
          <Link to="/overcalling-data-editing" className="mt-2 inline-block text-primary-600 hover:text-primary-800">
            Back to default view
          </Link>
        </div>
      )}

      {!showNotFound && activeItem && (
        <>
          {/* Metadata row — matches ProblemsPage's Number / Scoring / From row. */}
          <div className="hidden md:block narrow:hidden mt-6 short:mt-3 mb-4 short:mb-3">
            <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-wrap gap-4 short:gap-3 text-sm short:text-xs">
              <span>
                <span className="text-ink-soft">Number (ID):</span>{' '}
                <span className="font-semibold text-ink">{activeItem.tableResultId}</span>
              </span>
              <span className="text-red-500 font-bold">
                <span>Scoring:</span>{' '}
                <span>{activeItem.scoringType || 'N/A'}</span>
              </span>
              {activeItem.sourceLabel && (
                <span className="hidden md:inline narrow:hidden">
                  <span className="text-ink-soft">From:</span>{' '}
                  <span className="font-medium text-ink">{activeItem.sourceLabel}</span>
                </span>
              )}
              <span>
                <span className="text-ink-soft">Difficulty:</span>{' '}
                <span className="font-medium text-ink">
                  {activeItem.difficultyLevel != null ? `${activeItem.difficultyLevel}/10` : 'N/A'}
                </span>
              </span>
            </div>
          </div>

          {/* Main content - gray-100 wrapper matching ProblemsPage. `relative` anchors the
              FR-030a auto-advance mask overlay (below) that covers this whole container. */}
          <div className="relative bg-cream rounded-lg p-4 short:p-3 narrow:p-2 mb-3 short:mb-1 mobile-flex-col">
            <div className="flex flex-col flex-1 min-h-0 narrow:justify-start narrow:items-center lphone-outer">
              <div className="flex flex-wrap flex-1 min-h-0 gap-3 short:gap-2 narrow:gap-1.5 justify-center items-stretch narrow:flex-col narrow:flex-nowrap narrow:items-center narrow:justify-start lphone-items problem-items">

                {/* Vulnerability + Jeff's Hand — placement follows the hand's seat (data-seat), not the viewport */}
                <div className="flex gap-3 short:gap-2 narrow:gap-1.5 items-center flex-shrink-0 vuln-hand-group" data-seat={jeffSeat}>
                  <div className="flex-shrink-0">
                    <VulnerabilityIndicator
                      vulnerability={activeItem.vulnerability ?? 'None'}
                      decisionSeat={jeffSeat}
                      dealer={activeItem.fullAuction[0]?.seat as 'N' | 'E' | 'S' | 'W' | undefined}
                    />
                  </div>
                  <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex-shrink-0 w-[clamp(9rem,40vw,11rem)] md:w-[clamp(7rem,16.5vw,9rem)] lg:w-[clamp(8rem,18vw,12rem)] lphone-youhold">
                    <h3 className="font-semibold text-ink short:h-[1.75rem] short:text-[0.95rem]" style={{ fontSize: '1rem' }}>
                      Jeff holds <span className="text-ink-soft font-normal">(<span className="lg:hidden">{jeffSeat}</span><span className="hidden lg:inline">{DIRECTION_NAMES[jeffSeat]}</span>)</span>
                    </h3>
                    <HandDisplay hand={activeItem.jeffHand as PracticeHand} />
                  </div>
                </div>

                {/* Auction container — uses the FR-019a per-page wrapper which
                    masks Jeff's `jeffSeq` slot with a bold-red "?". The shared
                    AuctionTable component is NOT modified. */}
                <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-col auction-container-width">
                  <h3 className="font-semibold text-ink mb-1 short:mb-0.5 auction-header" style={{ fontSize: '1rem' }}>The Auction</h3>
                  <div className="auction-scroll scrollable flex-1">
                    {activeItem.fullAuction.length > 0 ? (
                      <OvercallingAuctionDisplay
                        fullAuction={activeItem.fullAuction}
                        jeffSeq={activeItem.jeffSeq}
                        vulnerability={activeItem.vulnerability ?? 'None'}
                        decisionSeat={jeffSeat}
                      />
                    ) : (
                      <div className="text-sm text-ink-soft">Auction not available.</div>
                    )}
                  </div>
                </div>

                {/* Answer column - mirrors "Result feedback" layout (FR-025). Holds the
                    "Jeff's bid" box and, stacked directly beneath it, an identically-styled
                    "Partner" box naming Jeff's partner (mapped from partnerId via PARTNER_NAMES).
                    The .feedback-container-width slot keeps its responsive column layout; the
                    border/background now live on the two inner boxes so the gray panel shows
                    in the gap between them.
                    Session 2026-05-20: "Jeff says:" section removed — was problem-only. */}
                <div
                  className="self-stretch narrow:flex-auto narrow:min-h-36 narrow:flex narrow:flex-col narrow:self-auto feedback-container-width gap-3 short:gap-2"
                >
                  <div className="border border-line bg-card rounded-lg p-3 short:p-2">
                    <div className="font-bold text-ink text-base short:text-sm">
                      Jeff&apos;s bid
                    </div>
                    <div className="mt-2 short:mt-1 text-lg short:text-base font-semibold text-ink">
                      {formatJeffBid(activeItem.jeffBid)}
                    </div>
                  </div>
                  <div className="border border-line bg-card rounded-lg p-3 short:p-2">
                    <div className="font-bold text-ink text-base short:text-sm">
                      Partner
                    </div>
                    <div className="mt-2 short:mt-1 text-lg short:text-base font-semibold text-ink">
                      {PARTNER_NAMES[activeItem.partnerId] ?? `Partner #${activeItem.partnerId}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FR-030a auto-advance mask (Session 2026-05-28 manual-test feedback; light mask
                per client sign-off 2026-05-29, mirroring the Assign Level of Difficulty page):
                while the timed advance is pending, cover the entire gray container with a light
                mask and show the Keep / Don't-Keep outcome plus "Moving to next hand ..." in
                large text, so it's obvious what happened and that the page is about to move on.
                Only fires in list mode — direct-link (?id=) curation never auto-advances, so
                `autoAdvancing` stays false there. */}
            {autoAdvancing && lastAction && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-[rgba(255,253,246,0.85)]"
                data-testid="auto-advance-mask"
                aria-live="polite"
              >
                <ConfirmationMessage lastAction={lastAction} />
              </div>
            )}
          </div>

          {/* Action row — Keep / Don't Keep on the left, Previous + Next on the right (FR-028, FR-029). */}
          <div className="flex flex-wrap gap-3 short:gap-2 mt-auto flex-shrink-0">
            <button
              type="button"
              onClick={onKeep}
              disabled={keepDisabled}
              className={`px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                keepDisabled
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              Keep
            </button>
            <button
              type="button"
              onClick={onReject}
              disabled={rejectDisabled}
              className={`px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                rejectDisabled
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              Don&apos;t Keep
            </button>
            <button
              type="button"
              onClick={onPrev}
              disabled={busy || !!directItem || atFirstItem}
              className={`ml-auto px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                busy || directItem || atFirstItem
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={busy || !!directItem || atLastItem}
              className={`px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
                busy || directItem || atLastItem
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Empty-state: no items in current filter. The message for "Not edited"
          is the "all reviewed" variant (there really is nothing uncurated left);
          every other filter just means the curated stores don't yet contain a
          row matching the filter's predicate, so use a plain "not available"
          message to avoid implying the editor has already reviewed items they
          never saw. */}
      {!showNotFound && !activeItem && !loading && (
        <div className="p-4 text-ink-soft">
          {filter === 'NotEdited'
            ? 'All items have been reviewed.'
            : 'No items available in this filter.'}
        </div>
      )}
    </div>
  );
}
